import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Rocket, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_CHECKLIST = [
  {
    category: 'Inhalte & Texte',
    items: [
      { id: 'txt-1', text: 'Alle Seitentexte finalisieren (DE/EN/FR)', done: false },
      { id: 'txt-2', text: 'Predigten mit Tags versehen (Glaube, Familie, Gebet...)', done: false },
      { id: 'txt-3', text: 'Mindestens 5 Predigten mit YouTube-Links eintragen', done: false },
      { id: 'txt-4', text: 'Veranstaltungsprogramm aktuell halten', done: false },
      { id: 'txt-5', text: 'Fotogalerie mit mind. 8 aktuellen Bildern befuellen', done: false },
      { id: 'txt-6', text: 'Kontaktdaten pruefen (Telefon, E-Mail, Adresse)', done: false },
    ]
  },
  {
    category: 'Design & Benutzererlebnis',
    items: [
      { id: 'ui-1', text: 'Mobile-Ansicht auf verschiedenen Geraeten testen', done: false },
      { id: 'ui-2', text: 'Desktop-Ansicht auf verschiedenen Browsern testen', done: false },
      { id: 'ui-3', text: 'Alle Bilder auf Ladezeit optimieren', done: false },
      { id: 'ui-4', text: 'Favicon und App-Icon setzen', done: false },
      { id: 'ui-5', text: 'OpenGraph-Bild fuer Social Media Vorschau', done: false },
    ]
  },
  {
    category: 'Technik & SEO',
    items: [
      { id: 'tech-1', text: 'SSL-Zertifikat aktiv (HTTPS)', done: false },
      { id: 'tech-2', text: 'Domain konfigurieren (z.B. icg-hannover.de)', done: false },
      { id: 'tech-3', text: 'Sitemap.xml generiert und bei Google eingereicht', done: false },
      { id: 'tech-4', text: 'robots.txt korrekt konfiguriert', done: false },
      { id: 'tech-5', text: 'Meta-Tags auf allen Seiten pruefen', done: false },
      { id: 'tech-6', text: 'Google Search Console einrichten', done: false },
      { id: 'tech-7', text: 'Google Analytics oder Tracking einrichten', done: false },
    ]
  },
  {
    category: 'Rechtliches',
    items: [
      { id: 'law-1', text: 'Impressum vollstaendig (DDG-konform)', done: false },
      { id: 'law-2', text: 'Datenschutzerklaerung aktuell (DSGVO)', done: false },
      { id: 'law-3', text: 'Cookie-Banner einrichten (falls noetig)', done: false },
      { id: 'law-4', text: 'AGB pruefen und veroeffentlichen', done: false },
    ]
  },
  {
    category: 'E-Mail & Newsletter',
    items: [
      { id: 'mail-1', text: 'SMTP-Versand testen (Yahoo Mail)', done: false },
      { id: 'mail-2', text: 'Samstags-Erinnerung (Cron Job) testen', done: false },
      { id: 'mail-3', text: 'Newsletter-Broadcast an Testgruppe senden', done: false },
      { id: 'mail-4', text: 'Kontaktformular-Empfang testen', done: false },
    ]
  },
  {
    category: 'Go-Live',
    items: [
      { id: 'live-1', text: 'Finale Pruefung aller Seiten durch Team', done: false },
      { id: 'live-2', text: 'Social-Media-Kanaele mit Website verlinken', done: false },
      { id: 'live-3', text: 'Gemeinde ueber den Launch informieren', done: false },
      { id: 'live-4', text: 'Website in Kirchenverzeichnissen eintragen', done: false },
      { id: 'live-5', text: 'QR-Code fuer Website erstellen (fuer Flyer/Plakate)', done: false },
    ]
  },
];

const AdminRelaunchTab = () => {
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST);
  const [expandedCats, setExpandedCats] = useState(
    DEFAULT_CHECKLIST.reduce((acc, cat) => ({ ...acc, [cat.category]: true }), {})
  );
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  // Load saved state from backend
  useEffect(() => {
    const loadChecklist = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/relaunch-checklist`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data && data.checklist) {
            // Merge saved state with default (in case new items were added)
            const savedMap = {};
            data.checklist.forEach(cat => {
              cat.items.forEach(item => {
                savedMap[item.id] = item.done;
              });
            });
            setChecklist(prev => prev.map(cat => ({
              ...cat,
              items: cat.items.map(item => ({
                ...item,
                done: savedMap[item.id] !== undefined ? savedMap[item.id] : item.done
              }))
            })));
          }
        }
      } catch {}
    };
    loadChecklist();
  }, []);

  const toggleItem = async (catIndex, itemIndex) => {
    const newChecklist = [...checklist];
    newChecklist[catIndex].items[itemIndex].done = !newChecklist[catIndex].items[itemIndex].done;
    setChecklist(newChecklist);

    // Save to backend
    try {
      await fetch(`${API_BASE}/admin/relaunch-checklist`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ checklist: newChecklist })
      });
    } catch {}
  };

  const toggleCategory = (cat) => {
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const totalItems = checklist.reduce((sum, cat) => sum + cat.items.length, 0);
  const doneItems = checklist.reduce((sum, cat) => sum + cat.items.filter(i => i.done).length, 0);
  const progress = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white rounded-xl shadow-lg p-6" data-testid="relaunch-progress">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Relaunch-Checkliste</h2>
            <p className="text-sm text-gray-500">{doneItems} von {totalItems} Aufgaben erledigt</p>
          </div>
          <div className="ml-auto text-right">
            <span className="text-3xl font-bold text-gray-900">{progress}%</span>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-500 bg-gradient-to-r from-blue-600 to-purple-600"
            style={{ width: `${progress}%` }}
            data-testid="relaunch-progress-bar"
          />
        </div>
      </div>

      {/* Categories */}
      {checklist.map((cat, catIndex) => {
        const catDone = cat.items.filter(i => i.done).length;
        const catTotal = cat.items.length;
        const isExpanded = expandedCats[cat.category];
        const catComplete = catDone === catTotal;

        return (
          <div key={cat.category} className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid={`relaunch-cat-${catIndex}`}>
            <button
              onClick={() => toggleCategory(cat.category)}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {catComplete ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-gray-400">{catDone}</span>
                  </div>
                )}
                <h3 className={`font-bold ${catComplete ? 'text-green-700' : 'text-gray-900'}`}>
                  {cat.category}
                </h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {catDone}/{catTotal}
                </span>
              </div>
              {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {isExpanded && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {cat.items.map((item, itemIndex) => (
                  <button
                    key={item.id}
                    onClick={() => toggleItem(catIndex, itemIndex)}
                    data-testid={`relaunch-item-${item.id}`}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-blue-50/50 transition-colors text-left"
                  >
                    {item.done ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                      {item.text}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminRelaunchTab;
